import { format, Locale, setMonth, setYear } from 'date-fns';
import { EventProps } from './Calendar';

export enum CalendarTimeInterval {
  TEN = 10,
  FIFTEEN = 15,
  THIRTY = 30,
  SIXTY = 60,
}

export enum CalendarDaysInterval {
  DAY = 'day',
  WEEK = 'week',
}

export function getIntervals(): CalendarTimeInterval[] {
  return Object.values(CalendarTimeInterval).filter(
    (value) => typeof value === 'number'
  ) as CalendarTimeInterval[];
}

export const getMonthYear = (
  month: number,
  year: number,
  locale: Locale
): string => {
  let date = new Date();
  date = setMonth(date, month);
  date = setYear(date, year);

  // Format the date to show only month and year in Czech
  return format(date, 'LLLL yyyy', { locale });
};

export const formatTimeRange = <ID>(
  event: EventProps<ID>,
  locale: Locale,
  simple: boolean
): string => {
  const start = event.startDate;
  const end = event.endDate;
  const fullDay = event.fullDay;

  if (simple) {
    const formattedStart = format(start, 'p', { locale });
    const formattedEnd = format(end, 'p', { locale });
    return `${formattedStart} - ${formattedEnd}`;
  } else {
    const sameDay =
      start.getDate() === end.getDate() &&
      start.getMonth() === end.getMonth() &&
      start.getFullYear() === end.getFullYear();
    if (sameDay) {
      if (fullDay) {
        const formatted = format(start, 'P', { locale });
        return formatted;
      } else {
        const formattedStart = format(start, 'EEEEEE P p', { locale });
        const formattedEnd = format(end, 'p', { locale });
        return `${formattedStart} - ${formattedEnd}`;
      }
    } else {
      if (fullDay) {
        const formattedStart = format(start, 'P', { locale });
        const formattedEnd = format(end, 'P', { locale });
        return `${formattedStart} - ${formattedEnd}`;
      } else {
        const formattedStart = format(start, 'Pp', { locale });
        const formattedEnd = format(end, 'Pp', { locale });
        return `${formattedStart} - ${formattedEnd}`;
      }
    }
  }
};

function getWeekStartDate(date: Date): Date {
  const day = date.getDay();
  const diff = (day === 0 ? -6 : 1) - day; // Adjust when the day is Sunday (0), otherwise adjust to Monday (1)
  const startOfWeek = new Date(date);
  startOfWeek.setDate(date.getDate() + diff);
  startOfWeek.setHours(0, 0, 0, 0); // Set time to start of the day
  return startOfWeek;
}

function getWeekNumber(date: Date): number {
  const startOfYear = new Date(date.getFullYear(), 0, 1);
  const pastDaysOfYear = Math.floor(
    (date.getTime() - startOfYear.getTime()) / 86400000
  );
  return Math.ceil((pastDaysOfYear + startOfYear.getDay() + 1) / 7);
}

export function findFullWeeksIndexes(dates: Date[]): number[] {
  const weekMap: { [key: string]: number[] } = {};
  const fullWeeks: number[] = [];

  // Group dates by the start of their week
  dates.forEach((date, index) => {
    const weekStart = getWeekStartDate(new Date(date))
      .toISOString()
      .split('T')[0]; // Get only the date part in YYYY-MM-DD format
    if (!weekMap[weekStart]) {
      weekMap[weekStart] = [];
    }
    weekMap[weekStart].push(index); // Push the index instead of the date
  });

  // Check for full weeks (7 days) and return their week numbers
  for (const week in weekMap) {
    if (weekMap[week].length === 7) {
      const fullWeekDate = new Date(week);
      const weekNumber = getWeekNumber(fullWeekDate);
      fullWeeks.push(weekNumber); // Collect week numbers of full weeks
    }
  }

  return fullWeeks; // Return all week numbers that have full weeks
}
